import appleIcon from "@ui/icons/brand/appleIcon.svg?raw";
import facebookIcon from "@ui/icons/brand/facebookIcon.svg?raw";
import googleIcon from "@ui/icons/brand/googleIcon.svg?raw";
import instagramIcon from "@ui/icons/brand/instagramIcon.svg?raw";
import threadsIcon from "@ui/icons/brand/threadsIcon.svg?raw";
import tiktokIcon from "@ui/icons/brand/tiktokIcon.svg?raw";
import xIcon from "@ui/icons/brand/xIcon.svg?raw";
import youtubeIcon from "@ui/icons/brand/youtubeIcon.svg?raw";

export const platformNames = {
	instagram: "Instagram",
	tiktok: "TikTok",
	facebook: "Facebook",
	x: "X",
	youtube: "YouTube",
} as const;

export type PlatformId = keyof typeof platformNames;

const platformIcons = {
	facebook: facebookIcon,
	instagram: instagramIcon,
	tiktok: tiktokIcon,
	youtube: youtubeIcon,
	x: xIcon,
	threads: threadsIcon,
	apple: appleIcon,
	google: googleIcon,
} as const;

export const normalizePlatform = (platform: string) => {
	const p = platform.toLowerCase();
	return p === "twitter" ? "x" : p;
};

export const getPlatformDisplayName = (platform: string) => {
	const key = normalizePlatform(platform);
	return platformNames[key as PlatformId] ?? platform;
};

export const allPlatforms = () =>
	Object.entries(platformNames).map(([value, label]) => ({ value, label }));

export const allPlatformValues = () => Object.keys(platformNames);

export const getPlatformIcon = (platform?: string | null) => {
	if (!platform) {
		return undefined;
	}
	return platformIcons[
		normalizePlatform(platform) as keyof typeof platformIcons
	];
};

/**
 * Windows puts a dialog's default button first. macOS, iOS, Android and GNOME
 * put it last.
 */
export const defaultButtonLeads = () => {
	const nav = navigator as Navigator & {
		userAgentData?: { platform: string };
	};
	return /^win/i.test(nav.userAgentData?.platform ?? nav.platform ?? "");
};

/** Platforms the `gvtlabs_color` font carries a colour logo glyph for. */
const platformColorGlyphs = new Set([
	"apple",
	"facebook",
	"google",
	"instagram",
	"threads",
	"tiktok",
	"x",
	"youtube",
]);

/**
 * The ligature that renders `platform`'s colour logo in `GVTColorSymbol`, or
 * undefined when the font has no glyph for it. Always the trailing-colon form,
 * which every glyph defines and which keeps a bare `x` from staying a letter
 * (see gvtlabs-font/font_source/gvtlabs_color/font_config.json).
 */
export const getPlatformColorGlyph = (platform?: string | null) => {
	if (!platform) {
		return undefined;
	}
	const normalized = normalizePlatform(platform);
	return platformColorGlyphs.has(normalized) ? `${normalized}:` : undefined;
};
